import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Paper,
  Alert,
  IconButton,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import {
  calculateLicensePrice,
  convertProformaToInvoice,
} from "../../../services/licenseService";
import { useNavigate } from "react-router-dom";

export default function LicensePurchaseForm() {
  const navigate = useNavigate();

  // فیلدهای فرم ورودی
  const [formData, setFormData] = useState({
    months: 12,
    coreQuantity: 1,
    enterpriseUsers: 5,
    functionalUsers: 10,
    customerPortalUsers: 0,
    agentPortalUsers: 0,
    additionalCompanies: 0,
  });

  // استیت‌های جریان پیش‌فاکتور و تأیید پرداخت
  const [quoteDetails, setQuoteDetails] = useState(null);
  const [priceLoading, setPriceLoading] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [priceError, setPriceError] = useState("");
  const [confirmError, setConfirmError] = useState("");
  const [paymentLoading, setPaymentLoading] = useState(false);

  // تغییر فیلدهای فرم
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: Math.max(0, parseInt(value, 10) || 0),
    }));
  };

  // اعتبارسنجی اولیه فرم
  const validateForm = () => {
    if (formData.months <= 0) {
      setPriceError("Duration (months) must be greater than 0.");
      return false;
    }
    if (formData.coreQuantity <= 0) {
      setPriceError("Core quantity must be at least 1.");
      return false;
    }
    return true;
  };

  // مرحله اول: محاسبه قیمت و دریافت پیش‌فاکتور (Proforma)
  const handleCalculatePrice = async (e) => {
    if (e) e.preventDefault();
    if (!validateForm()) return;

    try {
      setPriceLoading(true);
      setPriceError("");
      setConfirmError("");
      setQuoteDetails(null);

      // ساخت Payload بر اساس مقادیر وارد شده در فرم
      const payload = {
        months: String(formData.months ?? "0"),
        core_quantity: String(formData.coreQuantity ?? "0"),
        enterprise_user_quantity: String(formData.enterpriseUsers ?? "0"),
        functional_user_quantity: String(formData.functionalUsers ?? "0"),
        customer_portal_user_quantity: String(
          formData.customerPortalUsers ?? "0",
        ),
        agent_portal_user_quantity: String(formData.agentPortalUsers ?? "0"),
        additional_company_quantity: String(
          formData.additionalCompanies ?? "0",
        ),
        lt_currency_id: "1",
      };

      const res = await calculateLicensePrice(payload);
      const row = Array.isArray(res) ? res[0] : res;

      // بررسی وجود خطا در پاسخ دریافتی از سرور
      const hasError =
        row &&
        ((row.STATUS && row.STATUS !== "SUCCESS") ||
          (row.ERROR_NUMBER !== undefined &&
            String(row.ERROR_NUMBER) !== "0") ||
          (row.error !== undefined && String(row.error) !== "0"));

      if (hasError) {
        throw new Error(
          row.ERROR_MESSAGE ||
            row.message ||
            "Failed to calculate license price.",
        );
      }

      setQuoteDetails(row);
    } catch (error) {
      console.error("Calculate Price Error: ", error);
      setPriceError(error?.message || "Failed to calculate license price.");
    } finally {
      setPriceLoading(false);
    }
  };

  // نگاشت فیلدهای پاسخ سرور به ساختار خوانای جدول پیش‌فاکتور
  const getQuoteItems = (details) => {
    if (!details) return [];
    return [
      {
        key: "CORE",
        label: "Core License",
        quantity: details.CORE_QUANTITY ?? formData.coreQuantity,
        unitPrice: details.CORE_UNIT_PRICE ?? 0,
        total: details.CORE_TOTAL ?? 0,
      },
      {
        key: "ENTERPRISE",
        label: "Enterprise Users",
        quantity: details.ENTERPRISE_USER_QUANTITY ?? formData.enterpriseUsers,
        unitPrice: details.ENTERPRISE_USER_UNIT_PRICE ?? 0,
        total: details.ENTERPRISE_USER_TOTAL ?? 0,
      },
      {
        key: "FUNCTIONAL",
        label: "Functional Users",
        quantity: details.FUNCTIONAL_USER_QUANTITY ?? formData.functionalUsers,
        unitPrice: details.FUNCTIONAL_USER_UNIT_PRICE ?? 0,
        total: details.FUNCTIONAL_USER_TOTAL ?? 0,
      },
      {
        key: "CUSTOMER_PORTAL",
        label: "Customer Portal Users",
        quantity:
          details.CUSTOMER_PORTAL_USER_QUANTITY ?? formData.customerPortalUsers,
        unitPrice: details.CUSTOMER_PORTAL_USER_UNIT_PRICE ?? 0,
        total: details.CUSTOMER_PORTAL_USER_TOTAL ?? 0,
      },
      {
        key: "AGENT_PORTAL",
        label: "Agent Portal Users",
        quantity:
          details.AGENT_PORTAL_USER_QUANTITY ?? formData.agentPortalUsers,
        unitPrice: details.AGENT_PORTAL_USER_UNIT_PRICE ?? 0,
        total: details.AGENT_PORTAL_USER_TOTAL ?? 0,
      },
      {
        key: "ADDITIONAL_COMPANY",
        label: "Additional Companies",
        quantity:
          details.ADDITIONAL_COMPANY_QUANTITY ?? formData.additionalCompanies,
        unitPrice: details.ADDITIONAL_COMPANY_UNIT_PRICE ?? 0,
        total: details.ADDITIONAL_COMPANY_TOTAL ?? 0,
      },
    ].filter((item) => item.quantity > 0 || item.total > 0); // فقط مواردی که مقدار دارند نمایش داده شوند
  };

  // مرحله دوم: تأیید پیش‌فاکتور و صدور فاکتور نهایی و هدایت به درگاه پرداخت
  const handleConfirmProforma = async () => {
    try {
      setConfirmLoading(true);
      setConfirmError("");

      // ارسال درخواست نهایی به سرور بر اساس پیش‌فاکتور تایید شده
      const invoiceID = quoteDetails?.INVOICE_ID ;
      const result = await convertProformaToInvoice(invoiceID);

      const row = Array.isArray(result) ? result[0] : result;
      const hasError =
        row &&
        ((row.STATUS && row.STATUS !== "SUCCESS") ||
          (row.ERROR_NUMBER !== undefined && String(row.ERROR_NUMBER) !== "0"));

      if (hasError) {
        throw new Error(
          row.ERROR_MESSAGE || "Failed to generate payment bill.",
        );
      }

      // شبیه‌سازی هدایت به درگاه بانکی
      setPaymentLoading(true);
      setTimeout(() => {
        setPaymentLoading(false);
        // هدایت کاربر به صفحه کیف‌پول/تراکنش‌ها همراه با شماره پرداخت
        navigate("/dashboard/wallet");
      }, 2000);
    } catch (error) {
      console.error("Confirm Proforma Error: ", error);
      setConfirmError(
        error?.message || "Failed to confirm proforma and generate bill.",
      );
    } finally {
      if (!paymentLoading) {
        setConfirmLoading(false);
      }
    }
  };

  // بازگشت به فرم ویرایش مقادیر
  const handleBackToForm = () => {
    setQuoteDetails(null);
    setConfirmError("");
  };

  // لغو کل فرآیند پرداخت
  const handlePaymentCancel = () => {
    setQuoteDetails(null);
    setConfirmError("");
    setPriceError("");
    setPaymentLoading(false);
    setConfirmLoading(false);
  };

  const quoteItems = getQuoteItems(quoteDetails);

  return (
    <Box sx={{ width: "100%" }}>
      {/* مرحله اول: فرم پیکربندی و وارد کردن مقادیر */}
      {!quoteDetails && (
        <Card variant="outlined">
          <CardContent>
            <Typography
              variant="h5"
              gutterBottom
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <ShoppingCartIcon color="primary" /> License Purchase
              Configuration
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Configure the license items below to generate your official
              proforma invoice.
            </Typography>

            {priceError && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {priceError}
              </Alert>
            )}

            <Box component="form" onSubmit={handleCalculatePrice} noValidate>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Duration (Months)"
                    name="months"
                    type="number"
                    value={formData.months}
                    onChange={handleChange}
                    required
                    inputProps={{ min: 1 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Core License Quantity"
                    name="coreQuantity"
                    type="number"
                    value={formData.coreQuantity}
                    onChange={handleChange}
                    required
                    inputProps={{ min: 1 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Enterprise Users"
                    name="enterpriseUsers"
                    type="number"
                    value={formData.enterpriseUsers}
                    onChange={handleChange}
                    inputProps={{ min: 0 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Functional Users"
                    name="functionalUsers"
                    type="number"
                    value={formData.functionalUsers}
                    onChange={handleChange}
                    inputProps={{ min: 0 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Customer Portal Users"
                    name="customerPortalUsers"
                    type="number"
                    value={formData.customerPortalUsers}
                    onChange={handleChange}
                    inputProps={{ min: 0 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Agent Portal Users"
                    name="agentPortalUsers"
                    type="number"
                    value={formData.agentPortalUsers}
                    onChange={handleChange}
                    inputProps={{ min: 0 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Additional Companies"
                    name="additionalCompanies"
                    type="number"
                    value={formData.additionalCompanies}
                    onChange={handleChange}
                    inputProps={{ min: 0 }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box
                    sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}
                  >
                    <Button
                      variant="contained"
                      type="submit"
                      disabled={priceLoading}
                      startIcon={
                        priceLoading ? <CircularProgress size={20} /> : null
                      }
                      sx={{ minWidth: 150 }}
                    >
                      {priceLoading ? "Calculating..." : "Calculate Price"}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* مرحله دوم: پیش‌فاکتور صادر شده (Proforma Invoice) و تایید نهایی */}
      {quoteDetails && (
        <Card variant="outlined" sx={{ border: "1px solid var(--primary)" }}>
          <CardContent>
            {paymentLoading ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  py: 6,
                  gap: 2,
                }}
              >
                <CircularProgress size={50} />
                <Typography variant="h6">
                  Redirecting to Payment Gateway...
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Please do not refresh the page.
                </Typography>
                <Button
                  color="error"
                  variant="outlined"
                  onClick={handlePaymentCancel}
                  sx={{ mt: 2 }}
                >
                  Cancel Payment
                </Button>
              </Box>
            ) : (
              <>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <IconButton onClick={handleBackToForm} sx={{ mr: 1 }}>
                    <ArrowBackIcon />
                  </IconButton>
                  <Typography
                    variant="h5"
                    color="primary"
                    sx={{
                      flexGrow: 1,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <CheckCircleOutlineIcon color="success" /> Proforma Invoice
                    Summary
                  </Typography>
                </Box>

                {confirmError && (
                  <Alert severity="error" sx={{ mb: 3 }}>
                    {confirmError}
                  </Alert>
                )}

                <Paper
                  variant="outlined"
                  sx={{ p: 2, mb: 3, bgcolor: "var(--surface-muted)" }}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption" color="text.secondary">
                        DURATION (MONTHS)
                      </Typography>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {quoteDetails.MONTHS || formData.months}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption" color="text.secondary">
                        CURRENCY ID
                      </Typography>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {quoteDetails.CURRENCY_ID || "USD"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption" color="text.secondary">
                        TOTAL LICENSE PRICE
                      </Typography>
                      <Typography
                        variant="subtitle1"
                        fontWeight="bold"
                        color="primary.main"
                      >
                        {quoteDetails.TOTAL_PRICE?.toLocaleString() || "0"}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>

                <TableContainer
                  component={Paper}
                  variant="outlined"
                  sx={{ mb: 4 }}
                >
                  <Table size="small">
                    <TableHead sx={{ bgcolor: "var(--surface-muted)" }}>
                      <TableRow>
                        <TableCell>
                          <strong>Item Type</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>Quantity</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>Unit Price</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>Total Price</strong>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {quoteItems.map((row) => (
                        <TableRow key={row.key} hover>
                          <TableCell>{row.label}</TableCell>
                          <TableCell align="right">{row.quantity}</TableCell>
                          <TableCell align="right">
                            {row.unitPrice?.toLocaleString()}
                          </TableCell>
                          <TableCell align="right">
                            <strong>{row.total?.toLocaleString()}</strong>
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow>
                        <TableCell colSpan={3} align="right">
                          <strong>Total:</strong>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="subtitle2"
                            fontWeight="bold"
                            color="primary.main"
                          >
                            {quoteDetails.TOTAL_PRICE?.toLocaleString()}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 2,
                  }}
                >
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={handleBackToForm}
                    disabled={confirmLoading}
                  >
                    Edit Quantities
                  </Button>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                      variant="text"
                      color="error"
                      onClick={handlePaymentCancel}
                      disabled={confirmLoading}
                    >
                      Cancel Order
                    </Button>
                    <Button
                      variant="contained"
                      color="success"
                      onClick={handleConfirmProforma}
                      disabled={confirmLoading}
                      startIcon={
                        confirmLoading ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : null
                      }
                    >
                      {confirmLoading
                        ? "Confirming..."
                        : "Confirm & Proceed to Payment"}
                    </Button>
                  </Box>
                </Box>
              </>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
